import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Grid, Stack } from '@mui/material'
import React, { useContext } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'

import agent from 'agent'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import { StatusCheckConnectionForm } from 'components/status-checks/common/StatusCheckConnectionForm'
import { useGetStatusChecksUrls } from 'components/status-checks/hooks/use-get-status-checks-urls'
import {
  buildSaveStatusCheckRequest,
  getDefaultStatusCheckFormValuesFromStatusCheck,
  getStatusCheckFormValidationSchema,
  StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import {
  StatusCheckDetailsContext,
  StatusCheckDetailsProvider
} from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import {
  ObservabilityToolType,
  type ServiceStatusCheck,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { messageFromResponseError } from 'utils/strings'

const SaveStatusCheckButton: React.FC = () => {
  const { formState, watch } = useFormContext<StatusCheckFormValues>()
  const { dirtyFields } = formState

  const isOnlyPollingFieldDirty =
    dirtyFields.pollingIntervalSeconds && Object.keys(dirtyFields).length === 1

  const { isStatusCheckConnectionValidated, isStatusCheckEvaluationValidated } =
    useContext(StatusCheckDetailsContext)

  const selectedObservabilityToolType = watch('observabilityTool.type')

  // Should only validate the Success Evaluation when the observability tool type is custom
  const isSuccessEvaluationValid =
    selectedObservabilityToolType === ObservabilityToolType.CUSTOM
      ? isStatusCheckEvaluationValidated
      : true

  const isSaveStatusCheckButtonDisabled =
    !formState.isValid ||
    formState.isSubmitting ||
    // We want to enable the button if the form change did not require
    // manual validation of status check connections (polling interval value change).
    (!isOnlyPollingFieldDirty &&
      (!isSuccessEvaluationValid || !isStatusCheckConnectionValidated))

  return (
    <Button
      key="formSubmit"
      data-testid="edit-status-check-save-button"
      disabled={isSaveStatusCheckButtonDisabled}
      sx={{ width: 'fit-content' }}
      type="submit"
      variant="contained"
    >
      Save Health Check
    </Button>
  )
}

export const EditStatusCheckForm: React.FC<{
  existingStatusChecks?: ServiceStatusCheck[]
  externalIntegrations?: TeamExternalIntegration[]
  statusCheck: ServiceStatusCheck
  serviceName?: string
  headerContent?: React.ReactNode
}> = ({
  existingStatusChecks,
  externalIntegrations,
  statusCheck,
  serviceName,
  headerContent
}) => {
  const statusChecksUrls = useGetStatusChecksUrls()
  const navigate = useNavigate()
  const location = useLocation()

  const { referrer = statusChecksUrls.base() } = location.state ?? {}

  const isClone = statusCheck.id === null

  const { currentTeamId } = useAuth()

  const { refetch: reFetchStatusChecks } = useStatusChecksContext()

  const statusCheckFormValidationSchema = getStatusCheckFormValidationSchema(
    externalIntegrations,
    existingStatusChecks.filter(
      (existingStatusCheck) => existingStatusCheck.id !== statusCheck?.id
    )
  )

  const defaultValues: StatusCheckFormValues =
    getDefaultStatusCheckFormValuesFromStatusCheck(
      statusCheck,
      externalIntegrations
    )

  const form = useForm<StatusCheckFormValues>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(statusCheckFormValidationSchema)
  })

  const { control, formState, handleSubmit, setValue, trigger } = form

  const handleSaveStatusCheck = async (values: StatusCheckFormValues) => {
    try {
      const request = await buildSaveStatusCheckRequest(values, currentTeamId)

      // If we're cloning, create a new status check with POST
      // If we're editing, update the status check with PUT
      if (isClone) {
        await agent.post(`status-checks?teamId=${currentTeamId}`, request)
      } else {
        await agent.put(
          `status-checks/${statusCheck.id}?teamId=${currentTeamId}`,
          request
        )
      }

      toast({
        type: 'success',
        text: 'Health Check saved successfully.',
        time: 2_000
      })

      // Refresh status checks list
      await reFetchStatusChecks()

      // Navigate to back to status check list
      navigate(referrer)
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an issue saving this Health Check. Please try again later.'
      )
      toast({ type: 'error', text: error })
    }
  }

  return (
    <FormProvider {...form}>
      <StatusCheckDetailsProvider
        control={control}
        formState={formState}
        serviceName={serviceName}
        setValue={setValue}
        trigger={trigger}
      >
        <form onSubmit={handleSubmit(handleSaveStatusCheck)}>
          <Grid container>
            <Grid item lg={9} xs={12}>
              <Stack sx={{ rowGap: 3 }}>
                {headerContent}
                <StatusCheckConnectionForm />
                <Stack direction="row" sx={{ columnGap: 2 }}>
                  <SaveStatusCheckButton />
                  <Button
                    color="secondary"
                    component={StyledRouterLink}
                    to={referrer === '/' ? statusChecksUrls.list() : referrer}
                    variant="contained"
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </StatusCheckDetailsProvider>
    </FormProvider>
  )
}
